import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  DefaultButton,
  Icon,
} from "@fluentui/react";

import { useAuth } from "../../hooks/useAuth";
import { accessService } from "../../services/accessService";
import { SearchTrigger, SearchOverlay, usePortalSearch } from "./SearchOverlay";

type Props = { show?: boolean };

type NavItem = {
  key: string;
  label: string;
  path: string;
  adminOnly?: boolean;
};

const navItems: NavItem[] = [
  { key: "chat", label: "Chat", path: "/chat" },
  { key: "datasets", label: "Datasets", path: "/datasets" },
  { key: "dashboards", label: "Dashboards", path: "/dashboards" },
  { key: "dashboard-builder", label: "Build", path: "/dashboard-builder" },
  { key: "geospatial", label: "Geospatial", path: "/geospatial" },
  { key: "analytics", label: "Analytics", path: "/analytics" },
  { key: "realtime", label: "Real-Time", path: "/dashboards/realtime" },
  { key: "admin", label: "Admin", path: "/admin", adminOnly: true },
];

export default function TopNav({ show = true }: Props) {
  const nav = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();

  const [isAdmin, setIsAdmin] = useState(false);
  const [canAccessDashboard, setCanAccessDashboard] = useState(true);
  const [canBuildDashboard, setCanBuildDashboard] = useState(false);
  const [canAccessAnalytics, setCanAccessAnalytics] = useState(false);
  const [canAccessRealtime, setCanAccessRealtime] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open: searchOpen, openSearch, closeSearch } = usePortalSearch();

  useEffect(() => {
    // Don't fetch permissions until user is authenticated
    if (!isAuthenticated) return;

    let alive = true;
    setPermissionsLoaded(false);

    (async () => {
      // Probe admin status
      try {
        await accessService.get("/admin/users");
        if (alive) setIsAdmin(true);
      } catch (e: any) {
        if (!alive) return;
        if (e?.response?.status === 403) setIsAdmin(false);
      }

      // Fetch user profile for page access flags
      try {
        const res = await accessService.get<{
          role?: string;
          canAccessDashboard?: boolean;
          canBuildDashboard?: boolean;
          canAccessAnalytics?: boolean;
          canAccessRealtime?: boolean;
        }>("/me");
        if (!alive) return;
        const d = res.data;
        if (d.role === "admin") {
          setCanAccessDashboard(true);
          setCanBuildDashboard(true);
          setCanAccessAnalytics(true);
          setCanAccessRealtime(true);
        } else {
          setCanAccessDashboard(d.canAccessDashboard !== false);
          setCanBuildDashboard(d.canBuildDashboard === true);
          setCanAccessAnalytics(d.canAccessAnalytics === true);
          setCanAccessRealtime(d.canAccessRealtime === true);
        }
      } catch {
        // Default to showing all if /api/me fails
        if (alive) {
          setCanAccessDashboard(true);
          setCanBuildDashboard(true);
          setCanAccessAnalytics(false);
        }
      }

      if (alive) setPermissionsLoaded(true);
    })();
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const upn =
    (user as any)?.upn ||
    (user as any)?.email ||
    (user as any)?.username ||
    "";

  const userInitial = upn ? upn.charAt(0).toUpperCase() : "?";

  const visibleItems = useMemo(
    () =>
      navItems.filter((item) => {
        // While permissions are loading, show all non-admin tabs so nav isn't blank
        if (!permissionsLoaded) return !item.adminOnly;
        if (item.adminOnly && !isAdmin) return false;
        if (item.key === "dashboards" && !canAccessDashboard) return false;
        if (item.key === "dashboard-builder" && !canBuildDashboard) return false;
        if (item.key === "analytics" && !isAdmin && !canAccessAnalytics) return false;
        if (item.key === "realtime" && !canAccessRealtime) return false;
        return true;
      }),
    [isAdmin, canAccessDashboard, canBuildDashboard, canAccessAnalytics, canAccessRealtime, permissionsLoaded]
  );

  // Determine active nav item
  const activeKey = useMemo(() => {
    const path = location.pathname;
    if (path === "/chat") return "chat";
    // Match /agents, /agents/:id etc
    const match = visibleItems.find(
      (item) => item.path !== "/" && path.startsWith(item.path)
    );
    return match?.key || "home";
  }, [location.pathname, visibleItems]);

  if (!show) return null;

  return (
    <>
      <style>{`
        .topnav-outer { position: sticky; top: 0; z-index: 50; background: #FFFFFF; border-bottom: 1px solid #E1E1E1; }
        .topnav-inner { max-width: 1280px; margin: 0 auto; display: flex; align-items: center; padding: 0 16px; height: 52px; gap: 8px; }

        /* Brand — logo only on narrower screens, logo + wordmark when there's room */
        .topnav-brand { display: flex; align-items: center; gap: 10px; cursor: pointer; flex-shrink: 0; }
        .topnav-brand-text { font-size: 13px; color: #232323; font-weight: 700; letter-spacing: -0.01em; white-space: nowrap; }
        @media (max-width: 1180px) { .topnav-brand-text { display: none; } }

        /* Nav links — compact, no overflow scroll */
        .topnav-links {
          display: flex;
          align-items: center;
          gap: 1px;
          flex-shrink: 0;
          margin-left: 4px;
        }
        .topnav-link {
          background: transparent;
          border: none;
          border-radius: 4px;
          padding: 6px 10px;
          font-size: 13px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.12s ease;
          font-family: inherit;
          white-space: nowrap;
          color: #484848;
        }
        .topnav-link:hover { background: #f8f9fa; color: #232323; }
        .topnav-link-active { background: #d9f4d7; color: #2e873d; font-weight: 600; }
        .topnav-link-active:hover { background: #d9f4d7; color: #2e873d; }

        /* Search slot — flexes to fill the available middle, capped */
        .topnav-search-slot {
          flex: 1 1 auto;
          min-width: 180px;
          max-width: 440px;
          margin: 0 12px;
          display: flex;
          justify-content: center;
        }

        /* Right side */
        .topnav-right { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }

        .topnav-hamburger { display: none; background: none; border: 1px solid #E1E1E1; border-radius: 4px; width: 36px; height: 36px; cursor: pointer; align-items: center; justify-content: center; color: #232323; }
        .topnav-mobile-overlay { display: none; position: fixed; top: 52px; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.3); z-index: 49; }
        .topnav-mobile-menu { display: none; position: fixed; top: 52px; left: 0; right: 0; background: #FFFFFF; border-bottom: 1px solid #E1E1E1; z-index: 50; padding: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); max-height: calc(100vh - 52px); overflow-y: auto; }
        .topnav-mobile-link { display: flex; align-items: center; padding: 12px 16px; border-radius: 4px; font-size: 15px; font-weight: 500; cursor: pointer; color: #484848; border: none; background: none; width: 100%; text-align: left; font-family: inherit; }
        .topnav-mobile-link:hover { background: #f8f9fa; }
        .topnav-mobile-link-active { background: #d9f4d7; color: #2e873d; font-weight: 600; }
        .topnav-mobile-user { padding: 12px 16px; border-top: 1px solid #EDEDED; margin-top: 4px; display: flex; align-items: center; justify-content: space-between; }

        .topnav-avatar { width: 32px; height: 32px; border-radius: 50%; background: #2e873d; color: #FFFFFF; display: flex; align-items: center; justify-content: center; font-size: 14px; font-weight: 700; flex-shrink: 0; cursor: default; }
        .topnav-avatar-sm { width: 24px; height: 24px; border-radius: 50%; background: #2e873d; color: #FFFFFF; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }

        /* Narrow screens — fold links into the hamburger menu, free up
           horizontal room for the search bar to remain useful. */
        @media (max-width: 980px) {
          .topnav-links { display: none; }
          .topnav-search-slot { max-width: none; margin: 0 8px; }
        }

        @media (max-width: 768px) {
          .topnav-links { display: none; }
          .topnav-right .topnav-persona { display: none; }
          .topnav-right .topnav-signout { display: none; }
          .topnav-hamburger { display: flex; }
          .topnav-mobile-overlay.open { display: block; }
          .topnav-mobile-menu.open { display: block; }
        }
      `}</style>

      <div className="topnav-outer">
        <div className="topnav-inner">
          {/* Brand — logo always; "Enterprise Data Portal" wordmark only on
              wider screens to free space for the link row and search bar */}
          <div className="topnav-brand" onClick={() => nav("/")}>
            <img src="/logo-dark.svg" alt="City of Boroondara" style={{ height: 28 }} />
            <span className="topnav-brand-text">Enterprise Data Portal</span>
          </div>

          {/* Desktop Nav Links */}
          <div className="topnav-links">
            {visibleItems.map((item) => {
              const isActive = activeKey === item.key;
              return (
                <button
                  key={item.key}
                  onClick={() => nav(item.path)}
                  className={`topnav-link ${isActive ? "topnav-link-active" : ""}`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Search — its own slot so the pill has real room to breathe */}
          {isAuthenticated && (
            <div className="topnav-search-slot">
              <SearchTrigger onOpen={openSearch} />
            </div>
          )}

          {/* Right side */}
          <div className="topnav-right">
            <div className="topnav-persona" title={upn || "Signed in"}>
              <div className="topnav-avatar">{userInitial}</div>
            </div>

            <DefaultButton
              className="topnav-signout"
              text="Sign out"
              onClick={logout}
              styles={{
                root: {
                  borderRadius: 4,
                  border: "1px solid #E1E1E1",
                  background: "#F5F5F5",
                  color: "#232323",
                  fontSize: 12,
                  height: 32,
                  padding: "0 14px",
                },
                rootHovered: { background: "#EDEDED" },
              }}
            />

            {/* Hamburger (mobile only) */}
            <button
              className="topnav-hamburger"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <Icon iconName={mobileOpen ? "Cancel" : "GlobalNavButton"} style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile overlay */}
      <div
        className={`topnav-mobile-overlay ${mobileOpen ? "open" : ""}`}
        onClick={() => setMobileOpen(false)}
      />

      {/* Mobile menu */}
      <div className={`topnav-mobile-menu ${mobileOpen ? "open" : ""}`}>
        {visibleItems.map((item) => {
          const isActive = activeKey === item.key;
          return (
            <button
              key={item.key}
              onClick={() => { nav(item.path); setMobileOpen(false); }}
              className={`topnav-mobile-link ${isActive ? "topnav-mobile-link-active" : ""}`}
            >
              {item.label}
            </button>
          );
        })}
        <div className="topnav-mobile-user">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="topnav-avatar-sm">{userInitial}</div>
            <span style={{ fontSize: 12, color: '#232323' }}>{upn || "Signed in"}</span>
          </div>
          <DefaultButton
            text="Sign out"
            onClick={() => { logout(); setMobileOpen(false); }}
            styles={{
              root: {
                borderRadius: 4,
                border: "1px solid #E1E1E1",
                background: "#F5F5F5",
                color: "#232323",
                fontSize: 12,
                height: 32,
                padding: "0 14px",
              },
              rootHovered: { background: "#EDEDED" },
            }}
          />
        </div>
      </div>

      {/* Portal search overlay — Cmd/Ctrl-K to toggle, ESC to close */}
      <SearchOverlay open={searchOpen} onClose={closeSearch} />
    </>
  );
}
